import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useCrmAudit } from "@/hooks/use-crm-audit";
import { validateSignalAgentInput, type SignalAgentInput } from "@/lib/signal-agent-calc";
import type { DBSignalAgent } from "@/lib/db-types";

// Agentlisten er liten (ti systemagenter pluss egne), så plain select er trygt.

/** Alle agenter: systemagenter først, så etter navn. */
export function useSignalAgents(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["signal-agents"],
    queryFn: async (): Promise<DBSignalAgent[]> => {
      const { data, error } = await supabase
        .from("signal_agents")
        .select("*")
        .order("is_system", { ascending: false })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DBSignalAgent[];
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

// Audit skrives av DB-triggeren log_signal_agent_change med faktisk aktør.
const onError = (prefix: string) => (err: Error) => toast.error(`${prefix}: ${err.message}`);

/** Eier og created_by er alltid den innloggede brukeren (RLS krever det). */
export function useCreateSignalAgent() {
  const qc = useQueryClient();
  const { authUserId } = useCrmAudit();
  return useMutation({
    mutationFn: async (input: SignalAgentInput) => {
      const invalid = validateSignalAgentInput(input);
      if (invalid) throw new Error(invalid);
      if (!authUserId) throw new Error("Ikke innlogget");
      const { data, error } = await supabase
        .from("signal_agents")
        .insert({ ...input, name: input.name.trim(), instruction: input.instruction.trim(), owner_id: authUserId, created_by: authUserId })
        .select()
        .single();
      if (error) throw error;
      return data as DBSignalAgent;
    },
    onSuccess: () => invalidateFor(qc, "signal_agents"),
    onError: onError("Kunne ikke opprette agent"),
  });
}

export function useUpdateSignalAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<SignalAgentInput> & { id: string }) => {
      const invalid = validateSignalAgentInput(patch);
      if (invalid) throw new Error(invalid);
      const { data, error } = await supabase.from("signal_agents").update(patch).eq("id", id).select().maybeSingle();
      if (error) throw error;
      // RLS avviser uten feil (0 rader) når brukeren ikke eier agenten.
      if (!data) throw new Error("Du har ikke tilgang til å endre denne agenten");
      return data as DBSignalAgent;
    },
    onSuccess: () => invalidateFor(qc, "signal_agents"),
    onError: onError("Kunne ikke lagre agent"),
  });
}

/** Pause eller aktiver. Kostnadstak som er nådd pauser i run-signal-agents, ikke her. */
export function useToggleSignalAgent() {
  const update = useUpdateSignalAgent();
  return {
    ...update,
    mutate: (vars: { id: string; isActive: boolean }) => update.mutate({ id: vars.id, is_active: vars.isActive }),
  };
}

/** Treff siste 30 dager per agent (RPC crm_signal_agent_hits_30d), som agent-id til antall. */
export function useSignalAgentHits(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["signal-agents", "hits-30d"],
    queryFn: async (): Promise<Map<string, number>> => {
      const { data, error } = await supabase.rpc("crm_signal_agent_hits_30d");
      if (error) throw error;
      return new Map(((data ?? []) as { agent_id: string; hits: number }[]).map((r) => [r.agent_id, Number(r.hits)]));
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}
